/**
 * Outil « Traceur de courbes » (Boîte à outils, 3e → prépa).
 *
 * L'élève écrit f(x) comme sur sa copie (« ax² + bx + c »), chaque paramètre
 * a, b, c, k, m, p reçoit un curseur. Toucher le graphique lit f(x) à
 * l'endroit du doigt. Modèles proposés selon le niveau. Tracé SVG pur avec
 * les aides de `@jeraisonne/ui` (repère, graduations, courbe échantillonnée).
 */

import { useMemo, useRef, useState, type PointerEvent } from 'react';
import { RotateCcw, ZoomIn, ZoomOut } from 'lucide-react';
import { cn, sampleFunctionPath } from '@jeraisonne/ui';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import type { ToolProps } from '../../tool-types';
import { IconButton, ParamSlider } from '../../ui/controls';
import { makeFrame, type Viewport } from '../../ui/frame';
import { PlotGrid, FIGURE_BG_CLASS } from '../../ui/svg-parts';
import {
  compileFormula,
  defaultParam,
  evaluate,
  formatNumber,
  presetNote,
  PRESETS,
  type ParamName,
} from './formula';

const SIZE = 320;
const DEFAULT_VIEW: Viewport = { xMin: -5, xMax: 5, yMin: -5, yMax: 5 };
const SLIDER_MIN = -5;
const SLIDER_MAX = 5;

function zoom(vp: Viewport, factor: number): Viewport {
  const cx = (vp.xMin + vp.xMax) / 2;
  const cy = (vp.yMin + vp.yMax) / 2;
  const hx = ((vp.xMax - vp.xMin) / 2) * factor;
  const hy = ((vp.yMax - vp.yMin) / 2) * factor;
  return { xMin: cx - hx, xMax: cx + hx, yMin: cy - hy, yMax: cy + hy };
}

export function FunctionPlotterTool({ level }: ToolProps & { level?: string | null }) {
  const rank = schoolLevelRank(level);
  const presets = useMemo(() => PRESETS.filter((p) => rank === null || p.minRank <= rank), [rank]);
  const [formula, setFormula] = useState(() => presets[0]?.formula ?? 'ax + b');
  const [values, setValues] = useState<Partial<Record<ParamName, number>>>({});
  const [vp, setVp] = useState<Viewport>(DEFAULT_VIEW);
  const [x0, setX0] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const dragging = useRef(false);

  const compiled = useMemo(() => compileFormula(formula), [formula]);
  // une formule en cours d'écriture ne fait pas disparaître la dernière courbe
  const lastGood = useRef<{ expr: string; params: ParamName[] } | null>(null);
  if (compiled.ok) lastGood.current = { expr: compiled.expr, params: compiled.params };
  const shown = lastGood.current;

  const valueOf = (n: ParamName) => values[n] ?? defaultParam(n);
  const scope = useMemo(() => {
    const s: Partial<Record<ParamName, number>> = {};
    for (const n of shown?.params ?? []) s[n] = values[n] ?? defaultParam(n);
    return s;
  }, [shown, values]);

  const view = useMemo(() => makeFrame(vp, SIZE), [vp]);
  const path = useMemo(
    () =>
      shown
        ? sampleFunctionPath((x) => evaluate(shown.expr, x, scope), [vp.xMin, vp.xMax], view)
        : '',
    [shown, scope, vp, view],
  );

  const readAt = (e: PointerEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return;
    const r = svg.getBoundingClientRect();
    if (!r.width) return;
    const px = ((e.clientX - r.left) / r.width) * SIZE;
    const x = vp.xMin + (px / SIZE) * (vp.xMax - vp.xMin);
    // pas de 0,05 : des lectures qui tombent juste (f(1,5) plutôt que f(1,4973))
    setX0(Math.round(x * 20) / 20);
  };

  const y0 = x0 !== null && shown ? evaluate(shown.expr, x0, scope) : NaN;
  const yInView = Number.isFinite(y0) && y0 >= vp.yMin && y0 <= vp.yMax;
  const note = shown ? presetNote(formula, scope) : null;
  const axisY = vp.xMin <= 0 && vp.xMax >= 0 ? view.tx(0) : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2" role="group" aria-label="Modèles de fonctions">
        {presets.map((p) => (
          <button
            key={p.label}
            type="button"
            aria-pressed={formula === p.formula}
            onClick={() => {
              setFormula(p.formula);
              setValues({});
              setX0(null);
            }}
            className={cn(
              'min-h-11 rounded-xl border px-3 text-sm font-semibold transition-colors active:scale-[0.98]',
              formula === p.formula
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-ink-200 text-ink-600 hover:bg-ink-50',
            )}
          >
            {p.label}
          </button>
        ))}
      </div>

      <label className="block">
        <span className="text-ink-600 mb-1 block text-sm font-semibold">f(x) =</span>
        <input
          type="text"
          value={formula}
          onChange={(e) => setFormula(e.target.value)}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          inputMode="text"
          aria-invalid={!compiled.ok}
          aria-describedby="plotter-formula-help"
          className={cn(
            'text-ink-900 ocean:bg-ink-100 min-h-12 w-full rounded-xl border bg-white px-3 font-mono text-base',
            compiled.ok ? 'border-ink-200' : 'border-error-500',
          )}
        />
      </label>
      <p
        id="plotter-formula-help"
        className={cn('text-sm', compiled.ok ? 'text-ink-500' : 'text-error-600')}
        aria-live="polite"
      >
        {compiled.ok
          ? 'Écris comme sur ta copie : 2x + 1, ax², √x, sin(x)… Les lettres a, b, c, k, m, p ont un curseur.'
          : compiled.error}
      </p>

      {shown && shown.params.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {shown.params.map((n) => (
            <ParamSlider
              key={n}
              name={n}
              ariaLabel={`Valeur de ${n}`}
              value={valueOf(n)}
              min={SLIDER_MIN}
              max={SLIDER_MAX}
              step={0.1}
              format={(v) => formatNumber(v, 1)}
              onChange={(v) => setValues((old) => ({ ...old, [n]: v }))}
            />
          ))}
        </div>
      )}

      {note && (
        <p className="bg-ink-50 text-ink-800 rounded-2xl px-3 py-2 text-sm" aria-live="polite">
          {note}
        </p>
      )}

      <div className="mx-auto w-full max-w-[26rem]">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className={cn(
            'border-ink-100 block aspect-square w-full touch-none select-none rounded-2xl border',
            FIGURE_BG_CLASS,
          )}
          role="img"
          aria-label={`Courbe de f(x) = ${formula}`}
          onPointerDown={(e) => {
            dragging.current = true;
            e.currentTarget.setPointerCapture?.(e.pointerId);
            readAt(e);
          }}
          onPointerMove={(e) => {
            if (dragging.current) readAt(e);
          }}
          onPointerUp={() => (dragging.current = false)}
          onPointerCancel={() => (dragging.current = false)}
        >
          <PlotGrid frame={view} />
          <path d={path} fill="none" stroke="var(--color-primary-500)" strokeWidth={2.75} />
          {x0 !== null && x0 >= vp.xMin && x0 <= vp.xMax && (
            <g>
              <line
                x1={view.tx(x0)}
                y1={0}
                x2={view.tx(x0)}
                y2={SIZE}
                stroke="var(--color-accent-500)"
                strokeWidth={1.25}
                strokeDasharray="4 4"
              />
              {yInView && (
                <>
                  <line
                    x1={axisY ?? 0}
                    y1={view.ty(y0)}
                    x2={view.tx(x0)}
                    y2={view.ty(y0)}
                    stroke="var(--color-accent-500)"
                    strokeWidth={1.25}
                    strokeDasharray="4 4"
                  />
                  <circle cx={view.tx(x0)} cy={view.ty(y0)} r={6} fill="var(--color-accent-500)" />
                </>
              )}
            </g>
          )}
        </svg>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <p
          className="text-ink-900 min-w-0 flex-1 text-base font-semibold"
          aria-live="polite"
          data-testid="plotter-reading"
        >
          {x0 === null
            ? 'Touche le graphique pour lire une image.'
            : Number.isFinite(y0)
              ? `f(${formatNumber(x0, 2)}) ≈ ${formatNumber(y0)}`
              : `f(${formatNumber(x0, 2)}) n’existe pas`}
        </p>
        <IconButton label="Zoom avant" onClick={() => setVp((v) => zoom(v, 0.5))}>
          <ZoomIn className="h-5 w-5" aria-hidden />
        </IconButton>
        <IconButton label="Zoom arrière" onClick={() => setVp((v) => zoom(v, 2))}>
          <ZoomOut className="h-5 w-5" aria-hidden />
        </IconButton>
        <IconButton label="Recentrer" onClick={() => setVp(DEFAULT_VIEW)}>
          <RotateCcw className="h-5 w-5" aria-hidden />
        </IconButton>
      </div>
    </div>
  );
}
